import { useEffect, useState } from 'react';
import { heliumReceive } from 'utils/helpers';

const CLIP_NOT_FOUND_MSG = 'No clip found';
const HELP_MSG =
  'Most likely this means the event was deleted due to the retention period elapsing';

const clipPlaybackError = (t) => (
  <div className="op-flex-direction-column op-fully-centered-content">
    <div key="1" className="op-margin-bottom-22">
      {t(CLIP_NOT_FOUND_MSG)}
    </div>
    <div key="2">{t(HELP_MSG)}</div>
  </div>
);

export const useOpVideoPlayerData = ({
  orgId,
  opvideoDeviceId,
  currentIdentityLanguage,
  isLive,
  startAt,
  endAt,
  unixTimestamp,
  sendEvent,
  VideoEvents,
  isIncomingCall,
  setErrorProps,
  t,
}) => {
  const [playerValues, setPlayerValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load data needed for player once on mount
  useEffect(() => {
    sendEvent({ type: VideoEvents.ConnectionStart });

    // We don't run setup if we have bad data
    if (!isLive && !((startAt && endAt) || unixTimestamp)) {
      /*
        If a unixTimestamp doesn't exist this means we most likely got an
        invalid startTime from the URL param. This most likely means the event
        no longer exists due to the event being outside the retention period.
      */
      setErrorProps({
        errorMessage: clipPlaybackError(t),
        handleRetryClick: undefined,
      });
      console.error(
        'Neither startDate, endDate, or unixTimestamp were provided. Most likely this means that an invalid URL param `startTime` was provided (normally due the event being deleted as it is not within the retention period).',
      );

      sendEvent({ type: VideoEvents.ConnectionFailed });
      return;
    }

    // Do nothing if we don't have the orgId
    if (!orgId) {
      return;
    }

    // Player setup
    (async () => {
      const {
        data: opvideoDevice,
        localizedErrorMessage: describeOpvideoDeviceErrorMessage,
      } = await heliumReceive(
        'describeOpvideoDevice',
        [orgId, opvideoDeviceId],
        {
          headers: {
            'Accept-Language': currentIdentityLanguage,
          },
        },
      );

      if (describeOpvideoDeviceErrorMessage) {
        setErrorProps({ errorMessage: describeOpvideoDeviceErrorMessage });
        sendEvent({ type: VideoEvents.ConnectionFailed });
        return;
      }

      const { acuPort, cameraId } = opvideoDevice;
      const acuId = acuPort?.acu?.id;

      /*
        If we don't have an acuId something went wrong and we fail fast
        We don't need to show a player error here as this doesn't block playback/stream
      */
      if (!acuId || !cameraId) {
        console.error(
          'Something wrong with the opvideoDevice response object. No acuId or cameraId',
        );
        sendEvent({ type: VideoEvents.ConnectionFailed });
        return;
      }

      const [{ data: cameraStaleSnapshot }] = await Promise.all([
        heliumReceive('cameraStaleSnapshot', [orgId, cameraId], {
          headers: { 'Accept-Language': currentIdentityLanguage },
        }),
      ]);

      setPlayerValues((prevPlayerValues) => ({
        ...prevPlayerValues,
        opvideoDevice,
        cameraStaleSnapshot,
      }));

      sendEvent({ type: VideoEvents.ConnectionSuccessful });
      if (isIncomingCall) {
        sendEvent({ type: VideoEvents.IncomingCall });
      }
    })();
  }, [
    orgId,
    opvideoDeviceId,
    currentIdentityLanguage,
    isLive,
    startAt,
    endAt,
    unixTimestamp,
    sendEvent,
    VideoEvents,
    isIncomingCall,
    setErrorProps,
    t,
  ]);

  return {
    playerValues,
    isLoading,
    setIsLoading,
  };
};
